import { useBoxBuilder } from "@/lib/store";
import { formatUsd } from "@/lib/pricing";
import type { BoxTier } from "@/lib/types";
import { cn } from "@/lib/cn";

export default function TierSelector({ tiers }: { tiers: BoxTier[] }) {
  const tierVariationId = useBoxBuilder((s) => s.tierVariationId);
  const setTier = useBoxBuilder((s) => s.setTier);

  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      role="radiogroup"
      aria-label="Box size"
    >
      {tiers.map((tier) => {
        const selected = tierVariationId === tier.variationId;
        const disabled = !tier.inStock;
        return (
          <button
            key={tier.variationId}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => setTier(tier.variationId)}
            className={cn(
              "card relative flex items-center justify-between gap-3 px-4 py-3 text-left transition hover:-translate-y-0.5 hover:shadow-pop",
              selected && "bg-brand-50 ring-2 ring-brand-500",
              disabled && "cursor-not-allowed opacity-60 hover:translate-y-0",
            )}
          >
            <div className="flex min-w-0 items-baseline gap-3">
              <span
                className={cn(
                  "font-display text-lg sm:text-xl",
                  selected ? "text-brand-800" : "text-ink-800",
                )}
              >
                {tier.itemCount} items
              </span>
              <span
                className={cn(
                  "text-sm font-semibold",
                  selected ? "text-brand-700" : "text-ink-500",
                )}
              >
                {formatUsd(tier.basePriceCents)}
              </span>
            </div>
            {disabled ? (
              <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-ink-500">
                Sold out
              </span>
            ) : (
              <div
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold",
                  selected
                    ? "border-brand-500 bg-brand-500 text-white"
                    : "border-ink-200 text-ink-300",
                )}
                aria-hidden
              >
                {selected ? "\u2713" : ""}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
